import React, { useState, useCallback } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.jungleclick.online/api';

/* ──────────────────────────────────────────────── */
/*  Web Traffic — Google Trends Demand Analysis     */
/*  Port of tracking2-main/pages/traffic.py         */
/* ──────────────────────────────────────────────── */

function WebTraffic() {
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);     // { decomposition, seasonalAnalytics, keyword }
    const [showTable, setShowTable] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvName, setCsvName] = useState(null);

    const token = localStorage.getItem('accessToken');
    const authHeaders = { Authorization: `Bearer ${token}` };

    // ── Request mode ──
    const handleRequest = useCallback(async () => {
        if (!keyword.trim()) {
            setError('Please enter a keyword to use Request mode.');
            return;
        }
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            const res = await axios.post(
                `${API_BASE}/google-trends/demand-analysis`,
                { keyword: keyword.trim() },
                { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
            );
            if (res.data?.success) {
                setResult(res.data.data);
            } else {
                setError(res.data?.message || 'No data returned.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error fetching Google Trends data.');
        } finally {
            setLoading(false);
        }
    }, [keyword, authHeaders]);

    // ── Upload CSV mode ──
    const handleUploadCsv = useCallback(async () => {
        if (!csvFile) {
            setError('Please choose a CSV file exported from Google Trends.');
            return;
        }
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', csvFile);
            const res = await axios.post(
                `${API_BASE}/google-trends/upload-csv`,
                formData,
                { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } }
            );
            if (res.data?.success) {
                setResult(res.data.data);
            } else {
                setError(res.data?.message || 'Error parsing CSV.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error uploading CSV file.');
        } finally {
            setLoading(false);
        }
    }, [csvFile, authHeaders]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCsvFile(file);
            setCsvName(file.name);
        }
    };

    const handleClearCsv = () => {
        setCsvFile(null);
        setCsvName(null);
    };

    // ── Download decomposition as CSV ──
    const handleDownloadCsv = () => {
        if (!result?.decomposition) return;
        const rows = result.decomposition;
        const headers = ['Date', 'Original', 'Trend', 'Seasonal', 'Residual'];
        const csvContent = [
            headers.join(','),
            ...rows.map(r => [r.displayDate, r.originalValue, r.trend?.toFixed(4), r.seasonal?.toFixed(4), r.residual?.toFixed(4)].join(','))
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stl_${(result?.keyword || 'trends').replace(/\s+/g, '_')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const decomp = result?.decomposition || [];
    const analytics = result?.seasonalAnalytics || {};

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* ═══ HEADER ═══ */}
                <h2 className="fw-bold mb-1">
                    <i className="bi bi-globe2 me-2"></i>Demand Analysis
                </h2>
                <p className="text-muted mb-1">
                    <strong>Attention:</strong> popularity does not equal search volume, confirm with Search Query Performance.
                </p>
                <p className="text-muted small mb-4">
                    Google Trends (US, last 5y, en-US) → STL (Moving Avg.) → Plotly → Better decisions
                </p>

                {/* ═══ CONTROLS ═══ */}
                <div className="row mb-4 g-3 align-items-end">
                    <div className="col-md-4">
                        <label className="form-label fw-semibold">Keyword (required for Request mode)</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="e.g., rocket stove"
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRequest()}
                        />
                    </div>
                    <div className="col-md-auto">
                        <button className="btn btn-primary" onClick={handleRequest} disabled={loading}>
                            <i className="bi bi-search me-1"></i>Request
                        </button>
                    </div>
                    <div className="col-md-auto">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-secondary mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Choose CSV
                                <input type="file" accept=".csv,.tsv" hidden onChange={handleFileChange} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                            <button className="btn btn-outline-primary" onClick={handleUploadCsv} disabled={loading || !csvFile}>
                                <i className="bi bi-upload me-1"></i>Upload CSV
                            </button>
                        </div>
                    </div>
                </div>

                {/* ═══ LOADING / ERROR ═══ */}
                {loading && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <p className="mt-2 text-muted">Fetching Google Trends data…</p>
                    </div>
                )}
                {error && (
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
                    </div>
                )}

                {/* ═══ CHART 1: STL Decomposition (4 subplots) ═══ */}
                {decomp.length > 0 && (
                    <>
                        <div className="card mb-4 shadow-sm">
                            <div className="card-body">
                                <Plot
                                    data={[
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.originalValue), name: 'Original', mode: 'lines', xaxis: 'x', yaxis: 'y' },
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.trend), name: 'Trend', mode: 'lines', xaxis: 'x2', yaxis: 'y2' },
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.seasonal), name: 'Seasonal', mode: 'lines', xaxis: 'x3', yaxis: 'y3' },
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.residual), name: 'Residual', mode: 'lines', xaxis: 'x4', yaxis: 'y4' },
                                    ]}
                                    layout={{
                                        title: `STL Decomposition — ${result?.keyword || ''} — Google Trends (US, last 5y)`,
                                        height: 900,
                                        grid: { rows: 4, columns: 1, pattern: 'independent', roworder: 'top to bottom' },
                                        xaxis: { anchor: 'y', matches: 'x4' },
                                        xaxis2: { anchor: 'y2', matches: 'x4' },
                                        xaxis3: { anchor: 'y3', matches: 'x4' },
                                        xaxis4: { anchor: 'y4' },
                                        yaxis: { title: 'Original', domain: [0.78, 1.0] },
                                        yaxis2: { title: 'Trend', domain: [0.53, 0.75] },
                                        yaxis3: { title: 'Seasonal', domain: [0.28, 0.50] },
                                        yaxis4: { title: 'Residual', domain: [0.0, 0.23] },
                                        shapes: [
                                            { type: 'line', xref: 'x', yref: 'paper', x0: decomp[0].displayDate, x1: decomp[0].displayDate, y0: 0, y1: 1, line: { dash: 'dot', color: 'rgba(0,0,0,0.2)', width: 1 } },
                                            { type: 'line', xref: 'x', yref: 'paper', x0: decomp[decomp.length - 1].displayDate, x1: decomp[decomp.length - 1].displayDate, y0: 0, y1: 1, line: { dash: 'dot', color: 'rgba(0,0,0,0.2)', width: 1 } },
                                            { type: 'line', xref: 'x3', yref: 'y3', x0: decomp[0].displayDate, x1: decomp[decomp.length - 1].displayDate, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } },
                                            { type: 'line', xref: 'x4', yref: 'y4', x0: decomp[0].displayDate, x1: decomp[decomp.length - 1].displayDate, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } },
                                        ],
                                        margin: { l: 60, r: 30, t: 60, b: 40 },
                                    }}
                                    config={{ responsive: true }}
                                    style={{ width: '100%' }}
                                />
                                <p className="text-muted small mt-2">
                                    <em>How to read this:</em> the <strong>Trend</strong> shows the long-term direction, <strong>Seasonal</strong> the recurring intra-year pattern,
                                    and <strong>Residual</strong> the short-term irregular component (noise/outliers). Values are relative Google Trends interest.
                                </p>
                            </div>
                        </div>

                        {/* ═══ Expandable data table ═══ */}
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header d-flex align-items-center justify-content-between" style={{ cursor: 'pointer' }} onClick={() => setShowTable(!showTable)}>
                                <h6 className="mb-0">
                                    <i className={`bi bi-chevron-${showTable ? 'up' : 'down'} me-2`}></i>
                                    Show data (original/trend/seasonal/residual)
                                </h6>
                                <button className="btn btn-sm btn-outline-success" onClick={(e) => { e.stopPropagation(); handleDownloadCsv(); }}>
                                    <i className="bi bi-download me-1"></i>Download CSV
                                </button>
                            </div>
                            {showTable && (
                                <div className="card-body">
                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="table table-sm table-striped table-hover text-center">
                                            <thead className="table-dark sticky-top">
                                                <tr>
                                                    <th>Date</th><th>Original</th><th>Trend</th><th>Seasonal</th><th>Residual</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {decomp.map((row, i) => (
                                                    <tr key={i}>
                                                        <td>{row.displayDate}</td>
                                                        <td>{row.originalValue}</td>
                                                        <td>{row.trend?.toFixed(2)}</td>
                                                        <td>{row.seasonal?.toFixed(2)}</td>
                                                        <td>{row.residual?.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ═══ Additional seasonal analyses ═══ */}
                        <h4 className="fw-bold mt-5 mb-3">Additional seasonal analyses</h4>

                        {/* ═══ CHART 2: Average seasonal pattern by ISO week ═══ */}
                        {analytics.weeklyPattern?.length > 0 && (
                            <div className="card mb-4 shadow-sm">
                                <div className="card-body">
                                    {(() => {
                                        const weeklyX = analytics.weeklyPattern.map(d => d.isoWeek);
                                        const weeklyY = analytics.weeklyPattern.map(d => d.meanSeasonal);
                                        const weeklyText = weeklyX.map((w, i) => {
                                            const isKeyPoint = i === 0 || i === weeklyX.length - 1 || w % 8 === 0;
                                            return isKeyPoint ? `W${w}: ${Number(weeklyY[i]).toFixed(2)}` : '';
                                        });

                                        const weeklyMax = weeklyY.length
                                            ? weeklyY.reduce((best, v, i) => v > best.value ? { value: v, i } : best, { value: -Infinity, i: 0 })
                                            : null;
                                        const weeklyMin = weeklyY.length
                                            ? weeklyY.reduce((best, v, i) => v < best.value ? { value: v, i } : best, { value: Infinity, i: 0 })
                                            : null;

                                        return (
                                    <Plot
                                        data={[{
                                            x: weeklyX,
                                            y: weeklyY,
                                            mode: 'lines+markers+text',
                                            name: 'Mean Seasonal',
                                            line: { color: '#636EFA' },
                                            marker: { size: 6, color: '#636EFA' },
                                            text: weeklyText,
                                            textposition: 'top center',
                                            textfont: { size: 10, color: '#1f2937' },
                                            cliponaxis: false,
                                            hovertemplate: 'Week %{x}<br>Seasonal: %{y:.2f}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: 'Average seasonal pattern by ISO week (1–53)',
                                            height: 350,
                                            xaxis: { title: 'ISO week (1–53)', dtick: 4 },
                                            yaxis: { title: 'Seasonal value' },
                                            shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                            annotations: [
                                                ...(weeklyMax ? [{
                                                    x: weeklyX[weeklyMax.i],
                                                    y: weeklyY[weeklyMax.i],
                                                    text: `Peak: W${weeklyX[weeklyMax.i]} (${Number(weeklyY[weeklyMax.i]).toFixed(2)})`,
                                                    showarrow: true,
                                                    arrowhead: 2,
                                                    ax: 20,
                                                    ay: -25,
                                                    bgcolor: 'rgba(209, 250, 229, 0.9)',
                                                    bordercolor: '#10b981',
                                                    borderwidth: 1,
                                                    font: { size: 10, color: '#065f46' },
                                                }] : []),
                                                ...(weeklyMin ? [{
                                                    x: weeklyX[weeklyMin.i],
                                                    y: weeklyY[weeklyMin.i],
                                                    text: `Low: W${weeklyX[weeklyMin.i]} (${Number(weeklyY[weeklyMin.i]).toFixed(2)})`,
                                                    showarrow: true,
                                                    arrowhead: 2,
                                                    ax: -20,
                                                    ay: 25,
                                                    bgcolor: 'rgba(254, 226, 226, 0.9)',
                                                    bordercolor: '#ef4444',
                                                    borderwidth: 1,
                                                    font: { size: 10, color: '#991b1b' },
                                                }] : []),
                                            ],
                                            margin: { l: 60, r: 30, t: 50, b: 50 },
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                        );
                                    })()}
                                    <p className="text-muted small mt-2">
                                        <em>Interpretation:</em> this line shows the <strong>average seasonal effect</strong> at each ISO week across all years.
                                        Peaks indicate weeks that are typically above the baseline; troughs indicate below-baseline weeks.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ═══ CHART 3: Average seasonal pattern by month ═══ */}
                        {analytics.monthlyPattern?.length > 0 && (
                            <div className="card mb-4 shadow-sm">
                                <div className="card-body">
                                    {(() => {
                                        const monthlyX = analytics.monthlyPattern.map(d => d.monthLabel);
                                        const monthlyY = analytics.monthlyPattern.map(d => d.meanSeasonal);
                                        const monthlyText = monthlyX.map((m, i) => `${m}: ${Number(monthlyY[i]).toFixed(2)}`);

                                        return (
                                    <Plot
                                        data={[{
                                            x: monthlyX,
                                            y: monthlyY,
                                            mode: 'lines+markers+text',
                                            name: 'Mean Seasonal',
                                            marker: { size: 8 },
                                            line: { color: '#EF553B' },
                                            text: monthlyText,
                                            textposition: 'top center',
                                            textfont: { size: 10, color: '#1f2937' },
                                            cliponaxis: false,
                                            hovertemplate: 'Month %{x}<br>Seasonal: %{y:.2f}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: 'Average seasonal pattern by month',
                                            height: 350,
                                            xaxis: { title: 'Month' },
                                            yaxis: { title: 'Seasonal value' },
                                            shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                            margin: { l: 60, r: 30, t: 50, b: 50 },
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                        );
                                    })()}
                                    <p className="text-muted small mt-2">
                                        <em>Interpretation:</em> this summarizes the <strong>typical monthly seasonality</strong>.
                                        Use it to spot which months are usually stronger or weaker relative to the yearly baseline.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ═══ CHART 4: Seasonal distribution by month (box plot) ═══ */}
                        {analytics.monthlyDistribution?.length > 0 && (
                            <div className="card mb-4 shadow-sm">
                                <div className="card-body">
                                    <Plot
                                        data={analytics.monthlyDistribution.map(m => ({
                                            y: m.values,
                                            type: 'box',
                                            name: m.monthLabel,
                                            boxpoints: 'outliers',
                                        }))}
                                        layout={{
                                            title: 'Seasonal distribution by month',
                                            height: 400,
                                            xaxis: { title: 'Month' },
                                            yaxis: { title: 'Seasonal value' },
                                            shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                            showlegend: false,
                                            margin: { l: 60, r: 30, t: 50, b: 50 },
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                    <p className="text-muted small mt-2">
                                        <em>Interpretation:</em> boxes show the <strong>spread of seasonal values</strong> for each month across years.
                                        Wider boxes or longer whiskers mean more variability; outliers capture unusual months.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ═══ CHART 5: Year-over-year seasonality comparison ═══ */}
                        {analytics.yearOverYear?.length > 0 && (() => {
                            const years = [...new Set(analytics.yearOverYear.map(d => d.year))].sort();
                            const colors = ['#636EFA', '#EF553B', '#00CC96', '#AB63FA', '#FFA15A'];
                            return (
                                <div className="card mb-4 shadow-sm">
                                    <div className="card-body">
                                        <Plot
                                            data={years.map((yr, idx) => {
                                                const points = analytics.yearOverYear.filter(d => d.year === yr);
                                                const xVals = points.map(d => d.isoWeek);
                                                const yVals = points.map(d => d.seasonal);
                                                const textVals = xVals.map((_, i) =>
                                                    i === xVals.length - 1 ? `${yr}: ${Number(yVals[i]).toFixed(2)}` : ''
                                                );

                                                return {
                                                    x: xVals,
                                                    y: yVals,
                                                    mode: 'lines+markers+text',
                                                    name: String(yr),
                                                    line: { color: colors[idx % colors.length] },
                                                    marker: { size: 5 },
                                                    text: textVals,
                                                    textposition: 'middle right',
                                                    textfont: { size: 10, color: '#111827' },
                                                    cliponaxis: false,
                                                    hovertemplate: `Year ${yr}<br>Week %{x}<br>Seasonal: %{y:.2f}<extra></extra>`,
                                                };
                                            })}
                                            layout={{
                                                title: `Seasonality compared by year (${years[0]}–${years[years.length - 1]})`,
                                                height: 400,
                                                xaxis: { title: 'ISO week', dtick: 4 },
                                                yaxis: { title: 'Seasonal value' },
                                                shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                                margin: { l: 60, r: 30, t: 50, b: 50 },
                                            }}
                                            config={{ responsive: true }}
                                            style={{ width: '100%' }}
                                        />
                                        <p className="text-muted small mt-2">
                                            <em>Interpretation:</em> this compares <strong>seasonal curves across recent years</strong> on the same ISO-week axis.
                                            Look for alignment (stable seasonality) or divergences (shifts in timing or magnitude).
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ═══ Footer ═══ */}
                        <p className="text-muted text-center small mt-4">
                            Data source: Google Trends via <code>google-trends-api</code> • Decomposition: Moving Average • Charts: Plotly
                        </p>
                    </>
                )}
            </div>
        </Layout>
    );
}

export default WebTraffic;
